import { blue, green, orange, red } from '@ant-design/colors';
import { notification } from 'antd';
import type { TotalizadorListData } from '@/components/totalizador/TotalizadorList';
import type { ApiFinanceiroMovimento } from '@/features/financeiro-movimento/types/ApiFinanceiroMovimento';
import type { RootDispatch, RootState } from '@/state/store';
import type { ApiResponse } from '@/std/api/types';
import type { SimNao } from '@/types';
import type { Totalizador } from '@/types/schemas/Totalizador';
import { getEmpresaIdpk } from '@/features/login/util';
import { marcadoresToStringIdpk } from '@/features/marcador/util';
import { type BuildUrlParams, buildUrl } from '@/std/api/buildUrl';
import { comTokenGet, comTokenPost, comTokenPut, comTokenRemove } from '@/std/api/comToken';
import { isResponseErr, throwIfResponseIsErr } from '@/std/api/util';
import { endReduxFnError, endReduxFnOk, startReduxFn } from '@/std/redux';
import { TipoConsulta } from '@/types/enum';
import { Endpoint } from '@/types/enum/endpoint';
import { GetTypes } from '@/types/interfaces/GetTypes';
import type {
    ContasReceberFile,
    ContasReceberPostBody,
    ContasReceberPostLancamentoBody,
    ContasReceberPutBody,
} from './types';

const DATE_FORMAT = 'DD-MM-YYYY';

export const effects = (dispatch: RootDispatch) => ({
    async estornar(payload: { fmo_idpk: number; data_estorno?: string }): Promise<void> {
        dispatch.contasReceber.setState({
            estornar: startReduxFn(),
        });

        try {
            const { fmo_idpk, data_estorno } = payload;
            const params = { data_estorno, empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.FinanceiroMovimentoEstornar, params, fmo_idpk);
            const response: ApiResponse = await comTokenPut(url);
            throwIfResponseIsErr(response);

            void dispatch.contasReceber.get({
                type: GetTypes.editRecord,
            });

            dispatch.contasReceber.setState({
                estornar: endReduxFnOk(null),
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                estornar: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Falha ao estornar conta',
            });
        }
    },

    async estornarLancamento(payload: {
        fmo_idpk?: number;
        fml_idpk: number;
        fml_agrupado_id?: string;
        data_estorno?: string;
    }): Promise<void> {
        dispatch.contasReceber.setState({
            estornarLancamento: startReduxFn(),
        });

        try {
            const { fmo_idpk, fml_idpk, fml_agrupado_id, data_estorno } = payload;

            const params: BuildUrlParams = {
                data_estorno,
                empresa_idpk: getEmpresaIdpk(),
            };

            let url = '';

            if (fml_agrupado_id) {
                params.grupo_id = fml_agrupado_id;
                url = buildUrl(Endpoint.FinanceiroMovimentoLancamentoEstornar, params);
            } else {
                url = buildUrl(Endpoint.FinanceiroMovimentoLancamentoEstornar, params, fml_idpk);
            }

            const response: ApiResponse = await comTokenPut(url);
            throwIfResponseIsErr(response);

            if (fmo_idpk) {
                void dispatch.contasReceber.get({ type: GetTypes.firstGet });
            }

            dispatch.contasReceber.setState({
                estornarLancamento: endReduxFnOk(null),
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                estornarLancamento: endReduxFnError(error),
            });
        }
    },

    async excluirBoleto(payload: { idpk: number }, state: RootState): Promise<void> {
        dispatch.contasReceber.setState({
            excluirBoleto: startReduxFn(state.contasReceber.excluirBoleto.data),
        });

        try {
            const { idpk } = payload;

            const params = { empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.FinanceiroMovimentoBoleto, params, idpk);
            const response: ApiResponse = await comTokenRemove(url);
            throwIfResponseIsErr(response);

            dispatch.contasReceber.setState({
                excluirBoleto: endReduxFnOk(null),
            });

            notification.success({
                description: 'Boleto foi excluído',
                title: 'Sucesso!',
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                excluirBoleto: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Falha ao excluir boleto',
            });
        }
    },

    async excluirPixCobranca(payload: { idpk: number }, state: RootState): Promise<void> {
        dispatch.contasReceber.setState({
            excluirPixCobranca: startReduxFn(state.contasReceber.excluirPixCobranca.data),
        });

        try {
            const { idpk } = payload;

            const params = { empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.FinanceiroMovimentoPix, params, idpk);
            const response: ApiResponse = await comTokenRemove(url);
            throwIfResponseIsErr(response);

            dispatch.contasReceber.setState({
                excluirPixCobranca: endReduxFnOk(null),
            });

            notification.success({
                description: 'Pix cobrança foi excluído',
                title: 'Sucesso!',
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                excluirPixCobranca: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Falha ao excluir pix cobrança',
            });
        }
    },

    async gerarBoleto(
        {
            row,
            instrucao,
            numeroDocumento,
            conta,
            formaPagamentoIdpk,
        }: {
            row: ApiFinanceiroMovimento;
            instrucao: string;
            numeroDocumento: string;
            conta: number;
            formaPagamentoIdpk: number | null;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasReceber.setState({
            gerarBoleto: startReduxFn(state.contasReceber.gerarBoleto.data),
        });

        try {
            const body = {
                fmb_financeiro_conta_idpk: conta,
                fmb_forma_pagamento_idpk: formaPagamentoIdpk ?? undefined,
                fmb_instrucao: instrucao,
                fmb_numero_documento: numeroDocumento,
                fmb_valor: row.fmo_valor,
                fmb_vencimento: row.fmo_data_vencimento,
            };

            const params = {
                empresa_idpk: getEmpresaIdpk(),
                financeiro_movimento_idpk: row.fmo_idpk,
            };

            const url = buildUrl(Endpoint.FinanceiroMovimentoBoleto, params);
            const response: ApiResponse = await comTokenPost(url, body);

            if (isResponseErr(response)) {
                throw new Error(response.data.personalizado?.list[0]?.message || '');
            }

            dispatch.contasReceber.setState({
                gerarBoleto: endReduxFnOk(null),
            });

            notification.success({
                description: 'Boleto gerado com sucesso',
                title: 'Sucesso!',
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                gerarBoleto: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Falha ao gerar boleto',
            });
        }
    },

    async gerarPixCobranca(
        payload: {
            row: ApiFinanceiroMovimento;
            descricao: string;
            conta: number;
            formaPagamentoIdpk: number | null;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasReceber.setState({
            gerarPixCobranca: startReduxFn(state.contasReceber.gerarPixCobranca.data),
        });

        try {
            const { row, descricao, conta, formaPagamentoIdpk } = payload;

            const body = {
                fmp_cliente_idpk: row.fmo_cliente_idpk,
                fmp_descricao: descricao,
                fmp_financeiro_conta_idpk: conta,
                fmp_forma_pagamento_idpk: formaPagamentoIdpk ?? undefined,
                fmp_valor: row.fmo_valor,
                fmp_vencimento: row.fmo_data_vencimento,
            };

            const params = {
                empresa_idpk: getEmpresaIdpk(),
                financeiro_movimento_idpk: row.fmo_idpk,
            };

            const url = buildUrl(Endpoint.FinanceiroMovimentoPixCobranca, params);
            const response: ApiResponse = await comTokenPost(url, body);

            if (isResponseErr(response)) {
                throw new Error(response.data.personalizado?.list[0]?.message || '');
            }

            dispatch.contasReceber.setState({
                gerarPixCobranca: endReduxFnOk(null),
            });

            notification.success({
                description: 'Pix cobrança gerado com sucesso',
                title: 'Sucesso!',
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                gerarPixCobranca: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Falha ao gerar pix cobrança',
            });
        }
    },

    async get(
        payload: {
            type: GetTypes;
            customUrl?: string;
            fmo_idpk?: number;
            tipo_consulta?: TipoConsulta;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasReceber.setState({
            get: startReduxFn(state.contasReceber.get.data),
        });

        try {
            const { type, customUrl, fmo_idpk, tipo_consulta = TipoConsulta.Resumida } = payload;
            const { contasReceber } = state;

            const {
                filterStatus,
                filterSituacao,
                filterPesquisar,
                filterSacado,
                filterConta,
                filterValor,
                filterCategoria,
                filterDataEmissao,
                filterDataVencimento,
                filterDataQuitacao,
                filterMarcador,
                filterExcluidos,
                filterRenegociadas,
                filterContrato,
                filterVendedores,
                selectAll,
                pagination,
                sortParams,
            } = contasReceber;

            let newPagination = { ...pagination };

            // paginação precisa ser zerada antes de criar params
            if (
                type === GetTypes.recordFiltering ||
                type === GetTypes.addRecord ||
                type === GetTypes.singleRecord ||
                type === GetTypes.totalizadores
            ) {
                newPagination = {
                    current: 1,
                    pageSize: 10,
                    total: 0,
                };

                dispatch.contasReceber.setState({
                    pagination: newPagination,
                });
            }

            const params: BuildUrlParams = {
                contrato_idpk: filterContrato?.con_idpk,
                empresa_idpk: getEmpresaIdpk(),
                financeiro_categoria_idpk: filterCategoria?.fca_idpk,
                financeiro_conta_idpk: filterConta?.fco_idpk,
                marcadores: marcadoresToStringIdpk(filterMarcador),
                qtde_registros: newPagination.pageSize || 10,
                registro_inicial:
                    ((newPagination.current ?? 0) - 1) * (newPagination.pageSize || 0),
                total_registros: type === GetTypes.paginationChange ? 'N' : 'S',
                ...(sortParams.shouldSort
                    ? { orderby: `${sortParams.fieldName} ${sortParams.orderDirection}` }
                    : { orderby: 'fmo_data_vencimento desc' }),
                tipo_consulta,
                vendedor_idpk: filterVendedores?.map((v) => v.usu_idpk),
            };

            if (filterSacado?.fmo_cliente_idpk) {
                params.cliente_idpk = filterSacado.fmo_cliente_idpk;
            } else if (filterPesquisar) {
                params.pesquisar = filterPesquisar;
            }

            if (!filterStatus || filterStatus[0] === 'Total' || filterStatus.length === 0) {
                params.status = undefined;
            } else {
                params.status = filterStatus;
            }

            if (filterDataEmissao) {
                params.data_emissao_de = filterDataEmissao.start.format(DATE_FORMAT);
                params.data_emissao_ate = filterDataEmissao.end.format(DATE_FORMAT);
            }

            if (filterDataVencimento) {
                params.data_vencimento_de = filterDataVencimento.start.format(DATE_FORMAT);
                params.data_vencimento_ate = filterDataVencimento.end.format(DATE_FORMAT);
            }

            if (filterDataQuitacao) {
                params.data_quitacao_de = filterDataQuitacao.start.format(DATE_FORMAT);
                params.data_quitacao_ate = filterDataQuitacao.end.format(DATE_FORMAT);
            }

            if (filterValor) {
                params.valor_de = filterValor.start
                    ? filterValor.start.toString().trim().replace('.', ',')
                    : undefined;
                params.valor_ate = filterValor.end
                    ? filterValor.end.toString().trim().replace('.', ',')
                    : undefined;
            }

            if (filterSituacao && filterSituacao !== 0) {
                params.situacao = filterSituacao;
            }

            if (filterExcluidos) {
                params.excluidos = 'S';
            }

            if (filterRenegociadas) {
                params.renegociadas = 'S';
            }

            if (selectAll) {
                params.qtde_registros = undefined;
            }

            let url = '';

            if (type === GetTypes.customUrl && customUrl) {
                url = customUrl;
            } else if (type === GetTypes.singleRecord && fmo_idpk) {
                url = buildUrl(Endpoint.FinanceiroMovimentoContaReceber, params, fmo_idpk);
            } else {
                url = buildUrl(Endpoint.FinanceiroMovimentoContaReceber, params);
            }

            const response: ApiResponse = await comTokenGet(url);
            throwIfResponseIsErr(response);

            // paginação e filtros devem ser zerados após um get que alteraria a qtde de paginas
            if (
                type === GetTypes.firstGet ||
                type === GetTypes.addRecord ||
                type === GetTypes.totalizadores
            ) {
                dispatch.contasReceber.setState({
                    pagination: {
                        current: 1,
                        pageSize: 10,
                        showSizeChanger: Number(response.data.total_registros) > 10,
                        showTotal: () => `Total de Registros: ${response.data.total_registros}`,
                        total: response.data.total_registros
                            ? Number(response.data.total_registros)
                            : 0,
                    },
                });
            }

            // a paginação que já existe no state deve ser mantida, mas o total/showTotal deve mudar
            if (type === GetTypes.deleteRecord || type === GetTypes.recordFiltering) {
                dispatch.contasReceber.setState({
                    pagination: {
                        ...pagination,
                        showSizeChanger: Number(response.data.total_registros) > 10,
                        showTotal: () => `Total de Registros: ${response.data.total_registros}`,
                        total: response.data.total_registros
                            ? Number(response.data.total_registros)
                            : 0,
                    },
                });
            }

            dispatch.contasReceber.setState({
                get: endReduxFnOk(response.data.registros || []),
            });

            // todo get, exceto por paginacao, totalizadores e first-get com totalizador já cheio
            // (no caso de uma troca entre vendas-orçamentos-pedidos), deve gerar um novo totalizador
            if (
                [
                    GetTypes.addRecord,
                    GetTypes.deleteRecord,
                    GetTypes.editRecord,
                    GetTypes.recordFiltering,
                ].includes(type) ||
                (type === GetTypes.firstGet &&
                    contasReceber.getTotais.data &&
                    contasReceber.getTotais.data.length === 0)
            ) {
                void dispatch.contasReceber.getTotais(type);
            }
        } catch (error) {
            dispatch.contasReceber.setState({
                get: endReduxFnError(error),
            });
        }
    },

    async getOne(
        payload: { fmo_idpk: number },
        state: RootState,
    ): Promise<ApiFinanceiroMovimento | null> {
        dispatch.contasReceber.setState({
            getOne: startReduxFn(state.contasReceber.getOne.data),
        });

        const { fmo_idpk } = payload;

        const params = {
            empresa_idpk: getEmpresaIdpk(),
            tipo_consulta: TipoConsulta.Detalhada,
        };

        const url = buildUrl(Endpoint.FinanceiroMovimentoContaReceber, params, fmo_idpk);

        try {
            const response = await comTokenGet(url);
            throwIfResponseIsErr(response);

            const { data: { registros = [] } = {} } = response;
            const conta = registros[0] || null;

            dispatch.contasReceber.setState({
                getOne: endReduxFnOk(conta),
            });

            return conta;
        } catch (error) {
            dispatch.contasReceber.setState({
                getOne: endReduxFnError(error),
            });

            return null;
        }
    },

    async getTotais(type: GetTypes, state: RootState): Promise<void> {
        dispatch.contasReceber.setState({
            getTotais: startReduxFn(state.contasReceber.getTotais.data),
        });

        try {
            const { contasReceber } = state;

            const {
                filterStatus,
                filterSituacao,
                filterPesquisar,
                filterSacado,
                filterValor,
                filterCategoria,
                filterDataEmissao,
                filterDataVencimento,
                filterDataQuitacao,
                filterMarcador,
                filterExcluidos,
                filterRenegociadas,
                filterContrato,
                filterVendedores,
                selectAll,
                pagination,
            } = contasReceber;

            let status: string[] | undefined;

            if (!filterStatus || filterStatus[0] === 'Total' || filterStatus.length === 0) {
                status = undefined;
            } else {
                status = filterStatus;
            }

            let newPagination = { ...pagination };

            // paginação precisa ser zerada antes de criar params
            if (
                type === GetTypes.recordFiltering ||
                type === GetTypes.addRecord ||
                type === GetTypes.singleRecord ||
                type === GetTypes.totalizadores
            ) {
                newPagination = {
                    current: 1,
                    pageSize: 10,
                    total: 0,
                };
            }

            const params: BuildUrlParams = {
                contrato_idpk: filterContrato?.con_idpk,
                empresa_idpk: getEmpresaIdpk(),
                financeiro_categoria_idpk: filterCategoria ? filterCategoria.fca_idpk : undefined,
                marcadores: marcadoresToStringIdpk(filterMarcador),
                orderby: 'fmo_data_vencimento',
                qtde_registros: selectAll ? undefined : newPagination.pageSize || 10,
                registro_inicial:
                    ((newPagination.current ?? 0) - 1) * (newPagination.pageSize || 0),
                status,
                tipo: 'R',
                vendedor_idpk: filterVendedores?.map((v) => v.usu_idpk),
            };

            if (filterSacado?.fmo_cliente_idpk) {
                params.cliente_idpk = filterSacado.fmo_cliente_idpk;
            } else if (filterPesquisar) {
                params.pesquisar = filterPesquisar;
            }

            if (filterDataEmissao) {
                params.data_emissao_de = filterDataEmissao.start.format(DATE_FORMAT);
                params.data_emissao_ate = filterDataEmissao.end.format(DATE_FORMAT);
            }

            if (filterDataVencimento) {
                params.data_vencimento_de = filterDataVencimento.start.format(DATE_FORMAT);
                params.data_vencimento_ate = filterDataVencimento.end.format(DATE_FORMAT);
            }

            if (filterDataQuitacao) {
                params.data_quitacao_de = filterDataQuitacao.start.format(DATE_FORMAT);
                params.data_quitacao_ate = filterDataQuitacao.end.format(DATE_FORMAT);
            }

            if (filterValor) {
                params.valor_de = filterValor.start
                    ? filterValor.start.toString().trim().replace('.', ',')
                    : undefined;
                params.valor_ate = filterValor.end
                    ? filterValor.end.toString().trim().replace('.', ',')
                    : undefined;
            }

            if (filterSituacao && filterSituacao !== 0) {
                params.situacao = filterSituacao;
            }

            if (filterExcluidos) {
                params.excluidos = 'S';
            }

            if (filterRenegociadas) {
                params.renegociadas = 'S';
            }

            const url = buildUrl(Endpoint.FinanceiroMovimentoValoresTotais, params);
            const response: ApiResponse = await comTokenGet(url);
            throwIfResponseIsErr(response);

            const cores = {
                'A vencer': orange.primary,
                Recebidos: green.primary,
                'Total do período': blue.primary,
                'Vencem hoje': red[7],
                Vencidos: red.primary,
            };

            const totais: TotalizadorListData[] = (response.data.registros || []).map(
                (total: Totalizador) => ({
                    color: cores[total.status],
                    quantity: total.quantidade_total,
                    title: total.status,
                    total: total.valor_total,
                    value: total.situacao?.toString() ?? '0',
                }),
            );

            dispatch.contasReceber.setState({
                getTotais: endReduxFnOk(totais),
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                getTotais: endReduxFnError(error),
            });
        }
    },

    async post(
        payload: {
            agrupar: SimNao;
            body: ContasReceberPostBody;
            files: ContasReceberFile[];
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasReceber.setState({
            post: startReduxFn(state.contasReceber.post.data),
        });

        try {
            const { body, agrupar, files } = payload;

            const url = buildUrl(Endpoint.FinanceiroMovimentoContaReceber, {
                agrupar,
                empresa_idpk: getEmpresaIdpk(),
            });

            const response: ApiResponse = await comTokenPost(url, body);
            throwIfResponseIsErr(response);

            const fmo_idpk: number = response.data.registros?.[0]
                ? response.data.registros[0].fmo_idpk
                : 0;

            await dispatch.contasReceber.postAnexo({
                files,
                fmo_idpk,
            });

            dispatch.contasReceber.setState({
                post: endReduxFnOk(fmo_idpk),
                showModal: false,
            });

            void dispatch.contasReceber.get({
                type: GetTypes.addRecord,
            });

            notification.success({
                description: 'Conta cadastrada com sucesso',
                title: 'Feito!',
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                post: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Falhou!',
            });
        }
    },

    async postAnexo(
        payload: {
            fmo_idpk: number;
            files: ContasReceberFile[];
        },
        state: RootState,
    ): Promise<void> {
        const { fmo_idpk, files } = payload;

        // Caso não tiver arquivos simplesmente retorna
        if (files.length === 0) {
            dispatch.contasReceber.setState({
                postAnexo: endReduxFnOk(null),
            });

            return;
        }

        dispatch.contasReceber.setState({
            postAnexo: startReduxFn(state.contasReceber.postAnexo.data),
        });

        try {
            const params = {
                empresa_idpk: getEmpresaIdpk(),
                financeiro_movimento_idpk: fmo_idpk,
            };

            const url = buildUrl(Endpoint.FinanceiroMovimentoAnexo, params);
            const body = { registros: files };
            const response: ApiResponse = await comTokenPost(url, body);
            throwIfResponseIsErr(response);

            dispatch.contasReceber.setState({
                postAnexo: endReduxFnOk(null),
            });

            notification.success({
                description: 'Anexos foram cadastrados',
                title: 'Sucesso!',
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                postAnexo: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Falha ao cadastrar anexos',
            });
        }
    },

    async postLancamento(
        payload: {
            fmo_idpk: number;
            body: ContasReceberPostLancamentoBody;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasReceber.setState({
            postLancamento: startReduxFn(state.contasReceber.postLancamento.data),
        });

        try {
            const { fmo_idpk, body } = payload;

            const params = {
                empresa_idpk: getEmpresaIdpk(),
                financeiro_movimento_idpk: fmo_idpk,
            };

            const url = buildUrl(Endpoint.FinanceiroMovimentoLancamento, params);
            const response: ApiResponse = await comTokenPost(url, body);
            throwIfResponseIsErr(response);

            void dispatch.contasReceber.get({
                type: GetTypes.editRecord,
            });

            dispatch.contasReceber.setState({
                postLancamento: endReduxFnOk('success'),
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                postLancamento: endReduxFnError(error),
            });
        }
    },

    async put(
        payload: {
            fmo_idpk: number;
            body: ContasReceberPutBody;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasReceber.setState({
            put: startReduxFn(state.contasReceber.put.data),
        });

        try {
            const { fmo_idpk, body } = payload;

            const params = {
                empresa_idpk: getEmpresaIdpk(),
                grupo_parcelas_alterar: 'U',
            };

            const url = buildUrl(Endpoint.FinanceiroMovimentoContaReceber, params, fmo_idpk);
            const response: ApiResponse = await comTokenPut(url, body);
            throwIfResponseIsErr(response);

            void dispatch.contasReceber.get({
                type: GetTypes.editRecord,
            });

            dispatch.contasReceber.setState({
                put: endReduxFnOk('success'),
                showModal: false,
            });

            notification.success({
                description: 'Conta atualizada com sucesso',
                title: 'Feito!',
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                put: endReduxFnError(error),
            });

            notification.error({
                description: 'Falha ao atualizar conta',
                title: 'Falhou!',
            });
        }
    },

    async removeAnexo(
        payload: {
            fmo_idpk: number;
            anexo_idpk: number;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.contasReceber.setState({
            removeAnexo: startReduxFn(state.contasReceber.removeAnexo.data),
        });

        try {
            const { fmo_idpk, anexo_idpk } = payload;

            const params = {
                empresa_idpk: getEmpresaIdpk(),
                financeiro_movimento_idpk: fmo_idpk,
            };

            const url = buildUrl(Endpoint.FinanceiroMovimentoAnexo, params, anexo_idpk);
            const response: ApiResponse = await comTokenRemove(url);
            throwIfResponseIsErr(response);

            dispatch.contasReceber.setState({
                removeAnexo: endReduxFnOk(null),
            });
        } catch (error) {
            dispatch.contasReceber.setState({
                removeAnexo: endReduxFnError(error),
            });
        }
    },
});
